import React, { useCallback, useEffect, useState } from 'react';
import {
  createLscLeagueInvite,
  listLscLeagueInvites,
  revokeLscLeagueInvite,
  LscApiError,
} from '../api';
import { copyTextToClipboard } from '../copyToClipboard';
import {
  buildInviteClipboardText,
  canUseNativeShare,
  INVITE_COPIED_MESSAGE,
  INVITE_COPY_FAILED_MESSAGE,
  INVITE_SHARED_MESSAGE,
  resolveInviteShareUrl,
  shareInviteLink,
} from '../inviteShare';
import type { LscLeagueInvite } from '../types';
import ErrorBanner from './ErrorBanner';

interface LeagueInvitePanelProps {
  leagueId: string;
  leagueName?: string;
  leagueArchived?: boolean;
  refreshSignal?: number;
  /** Shared codes add a member with no team. Imported leagues must not render this panel. */
  unassignedJoin?: boolean;
}

type RowFeedback = {
  inviteId: string;
  message: string;
  tone: 'success' | 'error';
};

function statusBadgeClass(status?: string): string {
  if (status === 'active') return 'bg-emerald-900/50 text-emerald-200';
  if (status === 'revoked') return 'bg-red-900/40 text-red-200';
  if (status === 'expired') return 'bg-secondary-800 text-secondary-400';
  return 'bg-secondary-800 text-secondary-300';
}

function inviteRowId(invite: LscLeagueInvite): string {
  return invite.id || invite.invite_id || invite.code || '';
}

function inviteUsesLabel(invite: LscLeagueInvite): string {
  return invite.max_uses == null
    ? `${invite.used_count ?? 0} / ∞`
    : `${invite.used_count ?? 0} / ${invite.max_uses}`;
}

type InviteActionsProps = {
  invite: LscLeagueInvite;
  inviteId: string;
  isActive: boolean;
  showShareAction: boolean;
  revokingId: string | null;
  feedback: RowFeedback | null;
  onCopy: (invite: LscLeagueInvite) => void;
  onShare: (invite: LscLeagueInvite) => void;
  onRevoke: (inviteId: string) => void;
};

function InviteActions({
  invite,
  inviteId,
  isActive,
  showShareAction,
  revokingId,
  feedback,
  onCopy,
  onShare,
  onRevoke,
}: InviteActionsProps) {
  return (
    <>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="btn-secondary text-xs"
          onClick={() => onCopy(invite)}
          data-testid="copy-invite-button"
        >
          Copy invite
        </button>
        {showShareAction ? (
          <button
            type="button"
            className="btn-secondary text-xs"
            onClick={() => onShare(invite)}
            data-testid="share-invite-button"
          >
            Share
          </button>
        ) : null}
        {isActive ? (
          <button
            type="button"
            className="btn-secondary text-xs"
            disabled={revokingId === inviteId}
            onClick={() => onRevoke(inviteId)}
            data-testid="revoke-invite-button"
          >
            {revokingId === inviteId ? 'Revoking…' : 'Revoke'}
          </button>
        ) : null}
      </div>
      {feedback ? (
        <p
          className={`mt-2 text-xs ${feedback.tone === 'success' ? 'text-emerald-300' : 'text-red-300'}`}
          data-testid="invite-row-feedback"
        >
          {feedback.message}
        </p>
      ) : null}
    </>
  );
}

const LeagueInvitePanel: React.FC<LeagueInvitePanelProps> = ({
  leagueId,
  leagueName,
  leagueArchived = false,
  refreshSignal = 0,
  unassignedJoin = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [invites, setInvites] = useState<LscLeagueInvite[]>([]);
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [maxUses, setMaxUses] = useState(32);
  const [createFeedback, setCreateFeedback] = useState<string | null>(null);
  const [rowFeedback, setRowFeedback] = useState<RowFeedback | null>(null);

  const loadInvites = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listLscLeagueInvites(leagueId);
      setInvites(data.invites || []);
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not load invites.');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    void loadInvites();
  }, [loadInvites, refreshSignal]);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    setCreateFeedback(null);
    try {
      const result = await createLscLeagueInvite(leagueId, {
        expires_in_days: expiresInDays,
        max_uses: maxUses,
      });
      const invite = result.invite;
      if (invite?.code) {
        const shareUrl = resolveInviteShareUrl(invite);
        if (shareUrl) {
          const copied = await copyTextToClipboard(
            buildInviteClipboardText({
              url: shareUrl,
              code: invite.code,
              leagueName,
            })
          );
          setCreateFeedback(copied ? INVITE_COPIED_MESSAGE : INVITE_COPY_FAILED_MESSAGE);
        }
      }
      await loadInvites();
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not create invite.');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (inviteId: string) => {
    setRevokingId(inviteId);
    setError(null);
    setRowFeedback(null);
    try {
      await revokeLscLeagueInvite(leagueId, inviteId);
      await loadInvites();
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not revoke invite.');
    } finally {
      setRevokingId(null);
    }
  };

  const setFeedbackForRow = (inviteId: string, message: string, tone: 'success' | 'error') => {
    setRowFeedback({ inviteId, message, tone });
  };

  const handleCopyInvite = async (invite: LscLeagueInvite) => {
    const inviteId = inviteRowId(invite);
    const code = invite.code?.trim();
    const url = resolveInviteShareUrl(invite);
    if (!code || !url) {
      setFeedbackForRow(inviteId, INVITE_COPY_FAILED_MESSAGE, 'error');
      return;
    }
    const copied = await copyTextToClipboard(
      buildInviteClipboardText({ url, code, leagueName })
    );
    setFeedbackForRow(
      inviteId,
      copied ? INVITE_COPIED_MESSAGE : INVITE_COPY_FAILED_MESSAGE,
      copied ? 'success' : 'error'
    );
  };

  const handleShare = async (invite: LscLeagueInvite) => {
    const inviteId = inviteRowId(invite);
    const code = invite.code?.trim();
    const url = resolveInviteShareUrl(invite);
    if (!code || !url) {
      setFeedbackForRow(inviteId, INVITE_COPY_FAILED_MESSAGE, 'error');
      return;
    }

    const result = await shareInviteLink({ url, code, leagueName });
    if (result === 'shared') {
      setFeedbackForRow(inviteId, INVITE_SHARED_MESSAGE, 'success');
      return;
    }
    if (result === 'copied') {
      setFeedbackForRow(inviteId, INVITE_COPIED_MESSAGE, 'success');
      return;
    }
    if (result === 'failed') {
      setFeedbackForRow(inviteId, INVITE_COPY_FAILED_MESSAGE, 'error');
    }
  };

  const showShareAction = canUseNativeShare();

  const renderInviteFields = (invite: LscLeagueInvite, shareUrl: string | null) => (
    <>
      <div>
        <span className="text-xs text-secondary-500">Invite code</span>
        <code
          className="mt-1 block font-mono text-xs text-emerald-200 break-all whitespace-pre-wrap"
          data-testid="invite-code-value"
        >
          {invite.code ?? '—'}
        </code>
      </div>
      {shareUrl ? (
        <div>
          <span className="text-xs text-secondary-500">Invite URL</span>
          <a
            href={shareUrl}
            className="mt-1 block text-xs text-primary-300 break-all"
            data-testid="invite-link-value"
          >
            {shareUrl}
          </a>
        </div>
      ) : null}
    </>
  );

  return (
    <div className="card space-y-4" data-testid="league-invite-panel">
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">Invite Members</h2>
        <p className="text-sm text-secondary-400" data-testid="league-invite-purpose">
          {unassignedJoin
            ? "Joins members without a team. You'll assign teams afterwards."
            : 'Create shareable invite links for members. Invited players join immediately when they use the link or code.'}
        </p>
      </div>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {!leagueArchived && (
        <div className="space-y-3 border border-secondary-800 rounded-lg p-4">
          <h3 className="text-sm font-medium text-white">Create invite</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-secondary-400">Expires in (days)</span>
              <input
                type="number"
                min={1}
                max={90}
                className="input-field"
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(Number(e.target.value) || 30)}
                data-testid="invite-expires-days-input"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-secondary-400">Max uses</span>
              <input
                type="number"
                min={1}
                max={500}
                className="input-field"
                value={maxUses}
                onChange={(e) => setMaxUses(Number(e.target.value) || 32)}
                data-testid="invite-max-uses-input"
              />
            </label>
          </div>
          <button
            type="button"
            className="btn-primary"
            onClick={() => void handleCreate()}
            disabled={creating}
            data-testid="create-invite-button"
          >
            {creating ? 'Creating…' : 'Create invite'}
          </button>
          {createFeedback && (
            <p className="text-xs text-secondary-400" data-testid="invite-create-feedback">
              {createFeedback}
            </p>
          )}
        </div>
      )}

      {leagueArchived && (
        <p className="text-sm text-secondary-500" data-testid="invite-archived-note">
          New invites cannot be created for archived leagues. You can still revoke existing invites.
        </p>
      )}

      {loading ? (
        <p className="text-sm text-secondary-400">Loading invites…</p>
      ) : invites.length === 0 ? (
        <p className="text-sm text-secondary-500" data-testid="invite-list-empty">
          No invites yet.
        </p>
      ) : (
        <>
          <div className="space-y-3 md:hidden" data-testid="invite-list-cards">
            {invites.map((invite) => {
              const inviteId = inviteRowId(invite);
              const isActive = invite.status === 'active';
              const shareUrl = resolveInviteShareUrl(invite);
              const feedback = rowFeedback?.inviteId === inviteId ? rowFeedback : null;

              return (
                <div
                  key={inviteId}
                  className="rounded-lg border border-secondary-800 p-4 space-y-3"
                  data-testid="invite-list-card"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-xs capitalize ${statusBadgeClass(invite.status)}`}
                    >
                      {invite.status ?? 'unknown'}
                    </span>
                    <span className="text-xs text-secondary-400">Uses: {inviteUsesLabel(invite)}</span>
                  </div>
                  {renderInviteFields(invite, shareUrl)}
                  <dl className="grid grid-cols-2 gap-2 text-xs text-secondary-400">
                    <div>
                      <dt>Expires</dt>
                      <dd className="text-secondary-300">{invite.expires_at ?? '—'}</dd>
                    </div>
                    <div>
                      <dt>Created</dt>
                      <dd className="text-secondary-300">{invite.created_at ?? '—'}</dd>
                    </div>
                  </dl>
                  <InviteActions
                    invite={invite}
                    inviteId={inviteId}
                    isActive={isActive}
                    showShareAction={showShareAction}
                    revokingId={revokingId}
                    feedback={feedback}
                    onCopy={(item) => void handleCopyInvite(item)}
                    onShare={(item) => void handleShare(item)}
                    onRevoke={(id) => void handleRevoke(id)}
                  />
                </div>
              );
            })}
          </div>

          <div className="hidden md:block overflow-x-auto" data-testid="invite-list-table">
            <table className="min-w-full text-sm text-left text-secondary-300">
              <thead>
                <tr className="text-secondary-400 border-b border-secondary-700">
                  <th className="py-2 pr-4">Code / URL</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Expires</th>
                  <th className="py-2 pr-4">Uses</th>
                  <th className="py-2 pr-4">Created</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((invite) => {
                  const inviteId = inviteRowId(invite);
                  const isActive = invite.status === 'active';
                  const shareUrl = resolveInviteShareUrl(invite);
                  const feedback = rowFeedback?.inviteId === inviteId ? rowFeedback : null;

                  return (
                    <tr
                      key={inviteId}
                      className="border-b border-secondary-800 align-top"
                      data-testid="invite-list-row"
                    >
                      <td className="py-2 pr-4 min-w-[12rem] max-w-md">
                        {renderInviteFields(invite, shareUrl)}
                      </td>
                      <td className="py-2 pr-4">
                        <span
                          className={`inline-block px-2 py-0.5 rounded text-xs capitalize ${statusBadgeClass(invite.status)}`}
                        >
                          {invite.status ?? 'unknown'}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-xs">{invite.expires_at ?? '—'}</td>
                      <td className="py-2 pr-4">{inviteUsesLabel(invite)}</td>
                      <td className="py-2 pr-4 text-xs">{invite.created_at ?? '—'}</td>
                      <td className="py-2">
                        <InviteActions
                          invite={invite}
                          inviteId={inviteId}
                          isActive={isActive}
                          showShareAction={showShareAction}
                          revokingId={revokingId}
                          feedback={feedback}
                          onCopy={(item) => void handleCopyInvite(item)}
                          onShare={(item) => void handleShare(item)}
                          onRevoke={(id) => void handleRevoke(id)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default LeagueInvitePanel;
